import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Helmet } from "react-helmet-async";

import { Thumbs, FreeMode } from "swiper/modules";
import { getProductDetails } from "../api/products";
import { getImageUrl } from "../api/utils";
import { OK, showErrorMessage, sizesLabel, stripEmojis } from "../utils";
import { useCartStore } from "../store/cartStore";
import {
  useWishlistStore,
  getPreferredVariantId,
} from "../store/wishlistStore";
import ReviewsTab from "../components/shop/ReviewsTab";
import RelatedProducts from "../components/ProductSection/RelatedProducts";
import { addToRecentlyViewed } from "../hooks/useRecentlyViewed";

import RecentlyViewed from "../components/ProductSection/RecentlyViewed";
import SizeChartModal from "../components/ProductSection/SizeChartModal";

const FALLBACK_IMAGE = "/assets/images/product-placeholder.svg";

const getImageSrc = (image) => {
  if (!image) return null;
  if (typeof image === "string") return image;
  return image.image_url || image.url || image.image || null;
};

const formatPrice = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
  });
};

const Icon = ({ name, className = "" }) => {
  const icons = {
    // 'bag': '🛍️',
    // 'heart': '❤️',
    // 'delete': '✗',
    // 'compare': '⇄',
    // 'check': '✓',
    // 'view': '👁️',
    // 'arrow-left': '←',
    // 'arrow-right': '→',
  };
  return <i className={`icon icon-${name} ${className}`}>{icons[name]}</i>;
};

const StarRating = ({ rating, count }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <div style={{ display: "flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((s) => (
        <span
          key={s}
          style={{
            fontSize: 16,
            color: s <= Math.round(rating) ? "#f5a623" : "#ddd",
          }}
        >
          ★
        </span>
      ))}
    </div>
    <span style={{ fontSize: 13, color: "#888" }}>
      ({count} {count === 1 ? "review" : "reviews"})
    </span>
  </div>
);

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const swiperRef = useRef(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");

  const [sizeChartOpen, setSizeChartOpen] = useState(false);

  const addToCart = useCartStore((s) => s.addToCart);
  const wishlist = useWishlistStore((s) => s.wishlist);
  const addToWishlist = useWishlistStore((s) => s.addToWishlist);
  const removeFromWishlist = useWishlistStore((s) => s.removeFromWishlist);
  const loadingWishlistVariantId = useWishlistStore(
    (s) => s.loadingWishlistVariantId,
  );

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const handleSlideChange = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  // ── Load product ─────────────────────────────────────────
  useEffect(() => {
    console.log("slug:", slug);
    const load = async () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setLoading(true);
      setSelectedGender(null);
      setSelectedColor(null);
      setSelectedSize(null);
      setQuantity(1);
      setActiveTab("description");
      try {
        const res = await getProductDetails(slug);
        if (res?.status === "error") {
          showErrorMessage(res?.error);
          navigate("/shop");
          return;
        }
        setProduct(res?.product || res);
      } catch (err) {
        showErrorMessage(err || "Failed to load product");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  useEffect(() => {
    if (product?.slug) {
      addToRecentlyViewed(product.slug);
    }
  }, [product?.slug]);

  useEffect(() => {
    if (!product) return;

    // ── Google Analytics — view_item ─────────────────────
    if (window.gtag) {
      window.gtag("event", "view_item", {
        currency: "INR",
        value: Number(product.price || 0),
        items: [
          {
            item_id: String(product.id),
            item_name: stripEmojis(product.name),
            item_category: product.category?.name || "",
            price: Number(product.price || 0),
          },
        ],
      });
    }

    // ── Facebook Pixel — ViewContent ─────────────────────
    if (window.fbq) {
      window.fbq("track", "ViewContent", {
        value: Number(product.price || 0),
        currency: "INR",
        content_ids: [String(product.id)],
        content_name: stripEmojis(product.name),
        content_type: "product",
        content_category: product.category?.name || "",
      });
    }
  }, [product]);

  const variants = product?.product_variants || [];

  const genders = useMemo(
    () => [...new Set(variants.map((v) => v.gender).filter(Boolean))],
    [variants],
  );

  const colors = useMemo(() => {
    const filtered = variants.filter(
      (v) => !selectedGender || v.gender === selectedGender,
    );
    return [
      ...new Map(
        filtered.filter((v) => v.color).map((v) => [v.color.id, v.color]),
      ).values(),
    ];
  }, [variants, selectedGender]);

  const SIZE_ORDER = Object.keys(sizesLabel);

  const sizes = useMemo(
    () =>
      (product?.product_sizes || [])
        .map((ps) => ({
          id: ps.id,
          name: ps.name,
          selling_price: ps.selling_price,
          compare_price: ps.compare_price,
        }))
        .sort((a, b) => {
          const aIndex = SIZE_ORDER.indexOf(a.name);
          const bIndex = SIZE_ORDER.indexOf(b.name);
          // ── Agar size label mein nahi hai toh end mein daal do ──
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        }),
    [product],
  );
  const allImages = useMemo(() => {
    if (!product) return [];

    // ── Display images — PEHLE ────────────────────────────
    const displayImgs = (product.display_images || [])
      .filter((img) => img?.image?.url)
      .sort((a, b) => a.position - b.position)
      .map((img) => ({
        id: img.id,
        image_url: img.image.url,
        variantId: null,
        gender: null,
        colorId: null,
        colorName: null,
        isDisplay: true,
      }));

    // ── Variant images — BAAD MEIN ────────────────────────
    const variantImgs = variants.flatMap((v) =>
      (v.variant_images || [])
        .filter((img) => !!getImageSrc(img))
        .map((img) => ({
          ...img,
          variantId: v.id,
          gender: v.gender,
          colorId: v.color?.id,
          colorName: v.color?.name,
          isDisplay: false,
        })),
    );

    return [...displayImgs, ...variantImgs];
  }, [variants, product]);

  // ── Active index — selected gender+color ke pehle image pe jump karo ──
  const activeIndex = useMemo(() => {
    if (!selectedGender || !selectedColor) return 0;
    const idx = allImages.findIndex(
      (img) => img.gender === selectedGender && img.colorId === selectedColor,
    );
    return idx >= 0 ? idx : 0;
  }, [selectedGender, selectedColor, allImages]);

  const displayImages = useMemo(() => {
    if (!product) return [];
    if (selectedGender && selectedColor) {
      const filtered = variants.filter(
        (v) => v.gender === selectedGender && v.color?.id === selectedColor,
      );
      return filtered
        .flatMap((v) => v.variant_images || [])
        .filter((img) => !!getImageSrc(img));
    }
    return (variants[0]?.variant_images || []).filter(
      (img) => !!getImageSrc(img),
    );
  }, [product, variants, selectedGender, selectedColor]);

  // Auto-select defaults
  useEffect(() => {
    if (genders.length > 0 && !selectedGender) setSelectedGender(genders[0]);
  }, [genders]);
  useEffect(() => {
    if (colors.length > 0 && !selectedColor) setSelectedColor(colors[0]?.id);
  }, [colors, selectedColor]);
  useEffect(() => {
    if (!sizes.length) {
      setSelectedSize(null);
      return;
    }
    const exact = sizes.find((s) => s.name === selectedSize);
    if (!exact) setSelectedSize(sizes[0]?.name || null);
  }, [sizes]);

  const selectedVariant = variants.find(
    (v) =>
      (!selectedGender || v.gender === selectedGender) &&
      (!selectedColor || v.color?.id === selectedColor),
  );

  const selectedSizeObj = sizes.find((s) => s.name === selectedSize);
  const displayPrice =
    selectedSizeObj?.selling_price || sizes[0]?.selling_price;
  const displayComparePrice =
    selectedSizeObj?.compare_price || sizes[0]?.compare_price;
  const priceNum = Number(displayPrice);
  const compareNum = Number(displayComparePrice);
  const discount =
    compareNum > 0 && !Number.isNaN(priceNum)
      ? Math.round((1 - priceNum / compareNum) * 100)
      : 0;
  const inStock = !!selectedVariant && sizes.length > 0;

  const reviews = product?.reviews || [];
  const reviewsCount = Number(product?.reviews_count ?? reviews.length ?? 0);
  const category = product?.category || null;
  const collections = product?.collections || [];
  const preferredVariantId =
    selectedVariant?.id || getPreferredVariantId(product);
  const wishlistItem = preferredVariantId
    ? wishlist.find(
        (item) =>
          item.product_variant_id === preferredVariantId ||
          item.id === preferredVariantId,
      )
    : null;
  const isWishlisted = !!wishlistItem;
  const isWishlistLoading = loadingWishlistVariantId === preferredVariantId;

  const handleAddToCart = async () => {
    if (!inStock) return;
    const activeImageSrc = displayImages[0]
      ? getImageUrl(getImageSrc(displayImages[0]))
      : FALLBACK_IMAGE;

    const cartItem = {
      id: selectedVariant?.id || product.id,
      product_variant_id: selectedVariant?.id || product.id,
      product_id: product.id,
      slug: product.slug,
      title: stripEmojis(product.name),
      name: stripEmojis(product.name),
      price: Number(displayPrice) || 0,
      compare_price: Number(displayComparePrice) || 0,
      size: selectedSize,
      size_id: selectedSizeObj?.id,
      product_size_id: selectedSizeObj?.id,
      color: colors.find((c) => c.id === selectedColor)?.name,
      color_id: selectedColor,
      image: activeImageSrc,
    };

    // ── Await karo — sirf success pe navigate ────────────
    await addToCart(cartItem, quantity);

    // ── Check karo cart loading done hua aur error nahi aaya ──
    const { cartData } = useCartStore.getState();
    const justAdded = cartData?.items?.some(
      (i) => i.product_variant_id === cartItem.product_variant_id,
    );

    if (justAdded) {
      // ── Pixel + GA ────────────────────────────────────
      if (window.fbq) {
        window.fbq("track", "AddToCart", {
          value: Number(displayPrice) * quantity,
          currency: "INR",
          content_ids: [String(product.id)],
          content_name: stripEmojis(product.name),
          content_type: "product",
          contents: [
            {
              id: String(product.id),
              quantity,
              item_price: Number(displayPrice),
            },
          ],
        });
      }
      if (window.gtag) {
        window.gtag("event", "add_to_cart", {
          currency: "INR",
          value: Number(displayPrice) * quantity,
          items: [
            {
              item_id: String(product.id),
              item_name: stripEmojis(product.name),
              item_category: product.category?.name || "",
              price: Number(displayPrice),
              quantity,
            },
          ],
        });
      }
      navigate("/cart");
    }
  };

  const handleBuyNow = () => {
    if (!inStock) return;
    const activeImageSrc = displayImages[0]
      ? getImageUrl(getImageSrc(displayImages[0]))
      : FALLBACK_IMAGE;

    const cartItem = {
      id: selectedVariant?.id || product.id,
      product_variant_id: selectedVariant?.id || product.id,
      product_id: product.id,
      slug: product.slug,
      title: stripEmojis(product.name),
      name: stripEmojis(product.name),
      price: Number(displayPrice) || 0,
      compare_price: Number(displayComparePrice) || 0,
      size: selectedSize,
      size_id: selectedSizeObj?.id,
      product_size_id: selectedSizeObj?.id,
      color: colors.find((c) => c.id === selectedColor)?.name,
      color_id: selectedColor,
      image: activeImageSrc,
    };

    addToCart(cartItem, quantity);
    if (window.fbq) {
      window.fbq("track", "AddToCart", {
        value: Number(displayPrice) * quantity,
        currency: "INR",
        content_ids: [String(product.id)],
        content_name: stripEmojis(product.name),
        content_type: "product",
        contents: [
          {
            id: String(product.id),
            quantity: quantity,
            item_price: Number(displayPrice),
          },
        ],
      });
    }

    // ── Google Analytics — add_to_cart ───────────────────
    if (window.gtag) {
      window.gtag("event", "add_to_cart", {
        currency: "INR",
        value: Number(displayPrice) * quantity,
        items: [
          {
            item_id: String(product.id),
            item_name: stripEmojis(product.name),
            item_category: product.category?.name || "",
            price: Number(displayPrice),
            quantity: quantity,
          },
        ],
      });
    }
    navigate("/checkout");
  };

  const handleWishlistToggle = async () => {
    if (isWishlistLoading) return;
    if (!localStorage.getItem("Token")) {
      navigate("/login");
      return;
    }
    if (isWishlisted) {
      await removeFromWishlist(
        wishlistItem.wishlist_id || wishlistItem.id || preferredVariantId,
      );
    } else {
      await addToWishlist(preferredVariantId);
    }
  };

  // ── Loading ───────────────────────────────────────────────
  if (loading) {
    return (
      <section className="flat-spacing-4 pt_0">
        <div className="tf-main-product">
          <div className="container">
            <div className="row">
              <div className="col-md-6">
                <div
                  style={{
                    background: "#f4f3ef",
                    aspectRatio: "1/1",
                    animation: "pulse 1.4s ease-in-out infinite",
                  }}
                />
              </div>
              <div className="col-md-6">
                <div
                  style={{
                    background: "#f4f3ef",
                    height: 32,
                    width: "70%",
                    marginBottom: 16,
                    animation: "pulse 1.4s ease-in-out infinite",
                  }}
                />
                <div
                  style={{
                    background: "#f4f3ef",
                    height: 24,
                    width: "40%",
                    marginBottom: 24,
                    animation: "pulse 1.4s ease-in-out infinite",
                  }}
                />
                <div
                  style={{
                    background: "#f4f3ef",
                    height: 14,
                    width: "100%",
                    marginBottom: 8,
                    animation: "pulse 1.4s ease-in-out infinite",
                  }}
                />
                <div
                  style={{
                    background: "#f4f3ef",
                    height: 14,
                    width: "80%",
                    animation: "pulse 1.4s ease-in-out infinite",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!product) {
    return (
      <section className="flat-spacing-4 pt_0">
        <div
          className="container"
          style={{ textAlign: "center", padding: "80px 0" }}
        >
          <p style={{ color: "#888", marginBottom: 16 }}>Product not found.</p>
          <Link to="/shop" className="tf-btn btn-fill animate-hover-btn">
            ← Back to Shop
          </Link>
        </div>
      </section>
    );
  }

  const TABS = [
    {
      key: "description",
      label: "Description",
      content: (
        <p
          style={{ fontSize: 18, color: "#333", lineHeight: 1.5, margin: 0 }}
          dangerouslySetInnerHTML={{
            __html:
              product.description
                ?.replaceAll("\n", "<br />")
                .replaceAll("—", "-") || "",
          }}
        />
      ),
    },

    {
      key: "reviews",
      label: `Review (${reviewsCount})`,
      content: (
        <ReviewsTab
          reviews={reviews}
          reviewsCount={reviewsCount}
          productSlug={slug}
          onReviewSubmitted={(newReview) => {
            setProduct((prev) => ({
              ...prev,
              reviews: [newReview, ...(prev.reviews || [])],
              reviews_count: (Number(prev.reviews_count) || 0) + 1,
            }));
          }}
        />
      ),
    },
    {
      key: "shipping",
      label: "Shipping",
      content: (
        <div
          className="tf-page-privacy-policy"
          style={{ fontSize: 16, color: "#333", lineHeight: 1.2, margin: 0 }}
        >
          <p style={{ color: "#333" }}>
            Orders are processed within 3–5 business days.
          </p>
          <ul>
            <li>
              <strong>Metro Cities:</strong> 4–7 business days
            </li>
            <li>
              <strong>Tier 2 & 3 Cities:</strong> 6–10 business days
            </li>
            <li>
              <strong>Remote Areas:</strong> 10–14 business days
            </li>
          </ul>
          <br />
          <p style={{ color: "#333" }}>
            You will receive a tracking link via email once shipped. For
            queries:{" "}
            <a href="mailto:hello@huehoppers.com">hello@huehoppers.com</a>
          </p>
        </div>
      ),
    },
    {
      key: "returns",
      label: "Return Policies",
      content: (
        <div
          className="tf-page-privacy-policy"
          style={{ fontSize: 16, color: "#333", lineHeight: 1.2, margin: 0 }}
        >
          <p style={{ color: "#333" }}>
            Returns accepted within <strong>7 days</strong> of delivery. Items
            must be unused, unwashed with all tags intact.
          </p>
          <p style={{ color: "#333" }}>
            Customised or personalised products cannot be returned unless
            defective. For return requests email{" "}
            <a href="mailto:hello@huehoppers.com">hello@huehoppers.com</a> with
            subject: <em>Return Request – [Order ID]</em>
          </p>
        </div>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>{stripEmojis(product.name)} | HueHoppers – Kids' Fashion</title>
        <meta
          name="description"
          content={`${stripEmojis(product.name)} – ${product.design?.description?.slice(0, 150) || "Shop joyful kids' fashion at HueHoppers."}`}
        />
        <link
          rel="canonical"
          href={`https://huehoppers.com/products/${product.slug}`}
        />

        {/* ── OG Tags ── */}
        <meta
          property="og:title"
          content={`${stripEmojis(product.name)} | HueHoppers`}
        />
        <meta
          property="og:description"
          content={
            product.design?.description?.slice(0, 200) ||
            "Shop joyful kids' fashion at HueHoppers."
          }
        />
        <meta
          property="og:url"
          content={`https://huehoppers.com/products/${product.slug}`}
        />
        <meta property="og:type" content="product" />
        {/* ── OG Image — pehli display image ya pehli variant image ── */}
        <meta
          property="og:image"
          content={
            product.display_images?.[0]?.image?.url ||
            getImageUrl(getImageSrc(variants[0]?.variant_images?.[0])) ||
            ""
          }
        />

        {/* ── Product structured data ── */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: stripEmojis(product.name),
            description: product.design?.description || "",
            brand: { "@type": "Brand", name: "HueHoppers" },
            url: `https://huehoppers.com/products/${product.slug}`,
            image: product.display_images?.[0]?.image?.url || "",
            offers: {
              "@type": "Offer",
              priceCurrency: "INR",
              price: product.product_sizes?.[0]?.selling_price || 0,
              availability: "https://schema.org/InStock",
            },
            aggregateRating:
              product.reviews_count > 0
                ? {
                    "@type": "AggregateRating",
                    ratingValue: product.avg_rating,
                    reviewCount: product.reviews_count,
                  }
                : undefined,
          })}
        </script>
      </Helmet>

      <div className="tf-breadcrumb">
        <div className="container">
          <div className="tf-breadcrumb-wrap d-flex justify-content-between flex-wrap align-items-center">
            <div className="tf-breadcrumb-list">
              <Link to="/" className="text">
                Home
              </Link>
              <i className="icon icon-arrow-right" />
              {category && (
                <>
                  <Link to="/shop" className="text">
                    Shop
                  </Link>
                  <i className="icon icon-arrow-right" />
                </>
              )}
              <span className="text">
                {stripEmojis(product.name)} |{" "}
                {product.product_type === "TShirt"
                  ? "T-Shirt"
                  : product.product_type}
              </span>
            </div>
            <div className="tf-breadcrumb-prev-next">
              <Link
                to="/shop"
                className="tf-breadcrumb-back hover-tooltip center"
              >
                <i className="icon icon-shop" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Product Section ── */}
      <section className="flat-spacing-4 pt_0">
        <div className="tf-main-product section-image-zoom">
          <div className="container">
            <div className="row">
              {/* ── LEFT: Image Gallery ── */}
              <div className="col-md-6">
                <div className="tf-product-media-wrap sticky-top">
                  {displayImages.length > 0 ? (
                    <div className="thumbs-slider">
                      {/* Main swiper */}
                      <Swiper
                        onSwiper={(swiper) => {
                          swiperRef.current = swiper;
                        }}
                        spaceBetween={10}
                        modules={[FreeMode, Thumbs]}
                        className="tf-product-media-main"
                        navigation={{
                          nextEl: ".swiper-button-next",
                          prevEl: ".swiper-button-prev",
                        }}
                        id="gallery-swiper-started"
                        initialSlide={activeIndex}
                        onSlideChange={(swiper) => {
                          const img = allImages[swiper.activeIndex];
                          if (img && !img.isDisplay) {
                            if (img.gender !== selectedGender)
                              setSelectedGender(img.gender);
                            if (img.colorId !== selectedColor)
                              setSelectedColor(img.colorId);
                          }
                          setIsBeginning(swiper.isBeginning);
                          setIsEnd(swiper.isEnd);
                        }}
                      >
                        {allImages.map((img, i) => (
                          <SwiperSlide key={img.id || i}>
                            <div className="item">
                              <img
                                className="tf-image-zoom"
                                src={
                                  getImageUrl(getImageSrc(img)) ||
                                  FALLBACK_IMAGE
                                }
                                onError={(e) => {
                                  e.currentTarget.src = FALLBACK_IMAGE;
                                }}
                                alt={stripEmojis(product.name)}
                              />
                              {/* ── Color indicator ── */}
                              <div
                                style={{
                                  position: "absolute",
                                  bottom: 12,
                                  left: 12,
                                  background: "rgba(255,255,255,0.9)",
                                  padding: "3px 10px",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#2c3a34",
                                  borderRadius: 4,
                                }}
                              >
                                {img.gender === "male"
                                  ? "👦"
                                  : img.gender === "female"
                                    ? "👧"
                                    : ""}{" "}
                                {img.colorName}
                              </div>
                            </div>
                          </SwiperSlide>
                        ))}
                        {/* Nav buttons same */}
                        <div
                          className={`swiper-button-next button-style-arrow thumbs-next ${activeIndex === 0 ? "disabled" : ""}`}
                          role="button"
                          onClick={handleNext}
                          style={{
                            opacity: isBeginning ? 0.5 : 1,
                            cursor: isBeginning ? "not-allowed" : "pointer",
                          }}
                        />
                        <div
                          className={`swiper-button-prev button-style-arrow thumbs-prev`}
                          role="button"
                          onClick={handlePrev}
                          style={{
                            opacity: isEnd ? 0.5 : 1,
                            cursor: isEnd ? "not-allowed" : "pointer",
                          }}
                        />
                      </Swiper>
                    </div>
                  ) : (
                    // No images fallback
                    <div
                      style={{
                        background: "#f4f3ef",
                        aspectRatio: "1/1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 80,
                      }}
                    >
                      👕
                    </div>
                  )}
                </div>
              </div>

              {/* ── RIGHT: Product Info ── */}
              <div className="col-md-6">
                <div className="tf-product-info-wrap position-relative">
                  <div className="tf-product-info-list other-image-zoom">
                    {/* Title */}
                    <div className="tf-product-info-title">
                      <h5 className="">
                        {stripEmojis(product.name)} |{" "}
                        {product.product_type === "TShirt"
                          ? "T-Shirt"
                          : product.product_type}
                      </h5>
                      <p className="fs-16 pt-2">
                        <b>
                          👉 Soft, breathable cotton apparel your child will
                          reach for every day.
                        </b>
                      </p>
                    </div>
                    {/* Rating */}
                    <a
                      href="#product-details-tabs"
                      style={{
                        marginBottom: 12,
                      }}
                      onClick={() => {
                        setActiveTab("reviews");
                      }}
                    >
                      <StarRating
                        rating={Number(product.avg_rating ?? 0)}
                        count={reviewsCount}
                      />
                    </a>
                    {/* Price */}
                    <div className="tf-product-info-price">
                      <div
                        className="price-on-sale"
                        style={{ color: "#3a00ac" }}
                      >
                        ₹{formatPrice(displayPrice)}
                      </div>
                      {displayComparePrice &&
                        Number(displayComparePrice) > Number(displayPrice) && (
                          <div className="compare-at-price">
                            ₹{formatPrice(displayComparePrice)}
                          </div>
                        )}
                      {discount > 0 && (
                        <div
                          className="badges-on-sale"
                          style={{ backgroundColor: "#3a00ac" }}
                        >
                          <span>{discount}</span>% OFF
                        </div>
                      )}
                    </div>
                    {/* Short description */}
                    <div
                      className="product-status-content"
                      style={{
                        borderRadius: 4,
                        marginBottom: 30,
                      }}
                    >
                      <p
                        className="mb-0"
                        style={{ fontSize: 16, fontWeight: "normal" }}
                      >
                        ☀️ Family cart perk | ₹250 off on ₹1500+ orders with{" "}
                        <span className="fw-6">MOM250</span> at checkout. <br />
                        Valid for first 100 customers each month.
                      </p>
                      <p
                        className="mb-0"
                        style={{ fontSize: 16, fontWeight: "normal" }}
                      >
                        ☀️ First hop perk | 10% off your first order with{" "}
                        <span className="fw-6">HUEHOP10</span> at checkout.
                      </p>
                    </div>

                    {/* Variant Picker */}
                    <div className="tf-product-info-variant-picker">
                      {/* Gender */}
                      {genders.length > 0 && (
                        <div className="variant-picker-item">
                          <div className="variant-picker-label">
                            Gender:{" "}
                            <span className="fw-6 variant-picker-label-value">
                              {selectedGender === "male"
                                ? "Boy"
                                : selectedGender === "female"
                                  ? "Girl"
                                  : "Unisex"}
                            </span>
                          </div>
                          <div className="variant-picker-values">
                            {genders.map((gender) => (
                              <label
                                key={gender}
                                className={`style-text size-btn${selectedGender === gender ? " active" : ""}`}
                                onClick={() => {
                                  setSelectedGender(gender);
                                  setSelectedColor(null);
                                  setSelectedSize(null);
                                  // ── Swiper ko pehle image pe jump karo ───────────
                                  const idx = allImages.findIndex(
                                    (img) => img.gender === gender,
                                  );
                                  if (idx >= 0 && swiperRef.current) {
                                    swiperRef.current.slideTo(idx);
                                  }
                                }}
                                style={{
                                  cursor: "pointer",
                                  border:
                                    selectedGender === gender
                                      ? "2px solid #2c3a34"
                                      : "1px solid #ddd",
                                  padding: "6px 12px",
                                  borderRadius: 4,
                                }}
                              >
                                <p>
                                  {gender === "Male" || gender === "male"
                                    ? "👦 Boy"
                                    : gender === "Female" || gender === "female"
                                      ? "👧 Girl"
                                      : "👦 Unisex"}
                                </p>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Color */}
                      {colors.length > 0 && (
                        <div className="variant-picker-item">
                          <div className="variant-picker-label">
                            Color:{" "}
                            <span className="fw-6 variant-picker-label-value value-currentColor">
                              {colors.find((c) => c.id === selectedColor)
                                ?.name || ""}
                            </span>
                          </div>
                          <div className="variant-picker-values">
                            {colors.map((color) => (
                              <label
                                key={color.id}
                                className={`hover-tooltip radius-60 color-btn${selectedColor === color.id ? " active" : ""}`}
                                onClick={() => {
                                  setSelectedColor(color.id);
                                  // ── Swiper ko jump karo ──────────────────────────
                                  const idx = allImages.findIndex(
                                    (img) =>
                                      img.gender === selectedGender &&
                                      img.colorId === color.id,
                                  );
                                  if (idx >= 0 && swiperRef.current) {
                                    swiperRef.current.slideTo(idx);
                                  }
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <span
                                  className="btn-checkbox"
                                  style={{
                                    backgroundColor: color.hex_code || "#ccc",
                                    outline:
                                      selectedColor === color.id
                                        ? "2px solid #2c3a34"
                                        : "none",
                                    outlineOffset: 2,
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    display: "inline-block",
                                    boxShadow:
                                      color.hex_code === "#ffffff"
                                        ? "inset 0 0 0 1px #ddd"
                                        : "none",
                                  }}
                                />
                                <span className="tooltip">{color.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Size */}
                      {sizes.length > 0 && (
                        <div className="variant-picker-item">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="variant-picker-label">
                              Size:{" "}
                              <span className="fw-6 variant-picker-label-value">
                                {sizesLabel[selectedSize] || ""}
                              </span>
                            </div>
                            <a
                              onClick={() => setSizeChartOpen(true)}
                              href="#find_size"
                              data-bs-toggle="modal"
                              class="find-size fw-6"
                            >
                              Find your size
                            </a>
                          </div>
                          <div className="variant-picker-values">
                            {sizes
                              .sort((a, b) => a - b)
                              .map((sizeObj) => (
                                <label
                                  key={sizeObj.id}
                                  className={`style-text size-btn${selectedSize === sizeObj.name ? " active" : ""}`}
                                  onClick={() => setSelectedSize(sizeObj.name)}
                                  style={{
                                    cursor: "pointer",
                                    border:
                                      selectedSize === sizeObj.name
                                        ? "2px solid #2c3a34"
                                        : "1px solid #ddd",
                                    padding: "6px 12px",
                                    borderRadius: 4,
                                  }}
                                >
                                  <p>{sizesLabel[sizeObj.name]}</p>
                                </label>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Quantity */}
                    <div className="tf-product-info-quantity">
                      <div className="quantity-title fw-6">Quantity</div>
                      <div className="wg-quantity">
                        <span
                          className="btn-quantity btn-decrease"
                          onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        >
                          -
                        </span>
                        <input
                          type="text"
                          className="quantity-product"
                          name="number"
                          value={quantity}
                          readOnly
                        />
                        <span
                          className="btn-quantity btn-increase"
                          onClick={() => setQuantity((q) => q + 1)}
                        >
                          +
                        </span>
                      </div>
                    </div>
                    {/* Add to Cart */}
                    <div
                      className="tf-product-info-buy-button"
                      style={{ marginBottom: 10 }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <button
                          onClick={handleAddToCart}
                          disabled={!inStock}
                          className="tf-btn btn-fill justify-content-center fw-6 fs-16 flex-grow-1 animate-hover-btn"
                          style={{
                            opacity: !inStock ? 0.6 : 1,
                            cursor: !inStock ? "not-allowed" : "pointer",
                          }}
                        >
                          <span>
                            {inStock ? "Add to cart" : "Out of Stock"} •&nbsp;
                          </span>
                          <span className="tf-qty-price">
                            ₹{formatPrice(Number(displayPrice) * quantity)}
                          </span>
                        </button>

                        {/* Wishlist */}
                        <button
                          onClick={handleWishlistToggle}
                          disabled={isWishlistLoading}
                          className="tf-product-btn-wishlist hover-tooltip box-icon bg_white wishlist btn-icon-action"
                          style={{
                            border: "1px solid #ddd",
                            padding: 10,
                            cursor: isWishlistLoading
                              ? "not-allowed"
                              : "pointer",
                          }}
                        >
                          <span
                            className="icon icon-heart"
                            style={{
                              color: isWishlisted ? "#d32f2f" : undefined,
                            }}
                          />
                          <span className="tooltip">
                            {isWishlisted
                              ? "Remove from Wishlist"
                              : "Add to Wishlist"}
                          </span>
                        </button>
                      </div>
                    </div>
                    <div className="tf-product-info-buy-button">
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <button
                          onClick={handleBuyNow}
                          disabled={!inStock}
                          className="tf-btn btn-fill justify-content-center fw-6 fs-16 flex-grow-1 animate-hover-btn"
                          style={{
                            backgroundColor: "#ffc520",
                            borderColor: "#ffc520",
                            color: "#2c3a34",
                            opacity: !inStock ? 0.6 : 1,
                            cursor: !inStock ? "not-allowed" : "pointer",
                          }}
                        >
                          <span>
                            {inStock ? "Buy Now" : "Out of Stock"} •&nbsp;
                          </span>
                          <span className="tf-qty-price">
                            ₹{formatPrice(Number(displayPrice) * quantity)}
                          </span>
                        </button>

                        {/* Wishlist */}
                      </div>
                    </div>

                    {/* Delivery & Return info */}
                    {/* Trust seal + payments */}
                    <div style={{ marginBottom: 24 }}>
                      {/* ── Trust Bar ── */}
                      <img
                        style={{ width: "100%" }}
                        src={"/assets/images/pdp-trust.png"}
                        alt="Trust Seal"
                      />

                      {/* ── Product Attributes ── */}
                      <div>
                        {[
                          [
                            { label: "Fit", value: "Straight fit" },
                            { label: "Fabric", value: "100% pure cotton" },
                          ],
                          [
                            { label: "Build", value: "Durable, made to last" },
                            {
                              label: "Built for",
                              value: "Adventure | Everyday Comfort | Playtime",
                            },
                          ],
                        ].map((row, i) => (
                          <div
                            key={i}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              borderBottom: "1px solid #f0f0f0",
                              padding: "12px 0",
                              gap: 16,
                            }}
                          >
                            {row.map((attr, j) => (
                              <div key={j}>
                                <div
                                  style={{
                                    fontSize: 16,
                                    color: "#888",
                                    marginBottom: 4,
                                  }}
                                >
                                  {attr.label}
                                </div>
                                <div
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: "#2c3a34",
                                  }}
                                >
                                  {attr.value}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#888",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        marginTop: 16,
                      }}
                    >
                      {product?.product_type && (
                        <div>
                          <span style={{ fontWeight: 600, color: "#555" }}>
                            Type:{" "}
                          </span>
                          <Link
                            to={`/products?product_type=${product.product_type}`}
                            style={{
                              border: "1px solid #ddd",
                              padding: "2px 10px",
                              color: "#2c3a34",
                              textDecoration: "none",
                              fontSize: 13,
                              fontWeight: 600,
                            }}
                          >
                            {stripEmojis(product.product_type)}
                          </Link>
                        </div>
                      )}
                      {collections.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span style={{ fontWeight: 600, color: "#555" }}>
                            Collections:{" "}
                          </span>
                          {collections.map((c) => (
                            <Link
                              key={c.id}
                              to={`/collections/${c.slug}`}
                              style={{
                                border: "1px solid #ddd",
                                padding: "2px 10px",
                                color: "#2c3a34",
                                textDecoration: "none",
                                fontSize: 13,
                                fontWeight: 600,
                              }}
                            >
                              {stripEmojis(c.name)}
                            </Link>
                          ))}
                        </div>
                      )}

                      {product.design?.description && (
                        <p
                          style={{
                            fontSize: 18,
                            color: "#000",
                            lineHeight: 1.7,
                            marginBottom: 8,
                          }}
                          dangerouslySetInnerHTML={{
                            __html: product.design.description.replace(
                              /\n/g,
                              "<br />",
                            ),
                          }}
                        />
                      )}
                      <p
                        style={{
                          fontSize: 18,
                          color: "#000",
                          lineHeight: 1.7,
                          marginBottom: 8,
                        }}
                      >
                        Not just a tee. A mood your child will reach for every
                        single morning.
                      </p>
                    </div>

                    {/* Category + Collections */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="tf-sticky-btn-atc show">
          <div className="container">
            <div className="tf-height-observer w-100 d-flex align-items-center">
              <div className="tf-sticky-atc-product d-flex align-items-center">
                <div className="tf-sticky-atc-img">
                  <img
                    alt={stripEmojis(product.name)}
                    src={
                      displayImages[0]
                        ? getImageUrl(getImageSrc(displayImages[0]))
                        : FALLBACK_IMAGE
                    }
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_IMAGE;
                    }}
                  />
                </div>
                <div className="tf-sticky-atc-title fw-5 d-xl-block d-none">
                  <div className="fw-5" style={{ fontSize: 14 }}>
                    {stripEmojis(product.name)}
                  </div>
                  <div
                    style={{ fontSize: 13, color: "#3a00ac", fontWeight: 700 }}
                  >
                    ₹{formatPrice(Number(displayPrice) * quantity)}
                  </div>
                </div>
              </div>
              <div className="tf-sticky-atc-infos">
                <div
                  className=""
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <div className="tf-sticky-atc-variant-price d-flex align-items-center gap-2 flex-wrap">
                    <select
                      className="tf-select"
                      value={`${selectedGender}__${selectedColor}`}
                      onChange={(e) => {
                        const [gender, colorId] = e.target.value.split("__");
                        setSelectedGender(gender);
                        setSelectedColor(Number(colorId));
                      }}
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px 12px",
                        fontSize: 13,
                        color: "#2c3a34",
                        background: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      {[...variants]
                        .sort((a, b) => {
                          const order = { female: 0, male: 1, unisex: 2 };
                          return (
                            (order[a.gender] ?? 3) - (order[b.gender] ?? 3)
                          );
                        })
                        .map((v) => (
                          <option
                            key={`${v.gender}-${v.color?.id}`}
                            value={`${v.gender}__${v.color?.id}`}
                          >
                            {v.gender === "male"
                              ? "Boy"
                              : v.gender === "female"
                                ? "Girl"
                                : "Unisex"}{" "}
                            | {v.color?.name}
                          </option>
                        ))}
                    </select>

                    <select
                      className="tf-select"
                      value={selectedSize || ""}
                      onChange={(e) => setSelectedSize(e.target.value)}
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px 12px",
                        fontSize: 13,
                        color: "#2c3a34",
                        background: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      {sizes.map((sizeObj) => (
                        <option key={sizeObj.id} value={sizeObj.name}>
                          {sizesLabel[sizeObj.name] || sizeObj.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="tf-sticky-atc-btns">
                    <button
                      onClick={handleAddToCart}
                      disabled={!inStock}
                      className="tf-btn btn-fill justify-content-center fw-6 fs-14 animate-hover-btn"
                      style={{
                        opacity: !inStock ? 0.6 : 1,
                        cursor: !inStock ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {inStock ? "Add to Cart" : "Out of Stock"}
                    </button>

                    {inStock && (
                      <button
                        onClick={handleBuyNow}
                        className="tf-btn justify-content-center fw-6 fs-14 animate-hover-btn"
                        style={{
                          backgroundColor: "#f5c518",
                          color: "#000",
                          border: "none",
                          whiteSpace: "nowrap",
                          cursor: "pointer",
                        }}
                      >
                        Buy Now
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="flat-spacing-17 pt_0" id="product-details-tabs">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="widget-tabs style-has-border">
                <ul className="widget-menu-tab">
                  {TABS.map((tab) => (
                    <li
                      key={tab.key}
                      className={`item-title${activeTab === tab.key ? " active" : ""}`}
                      onClick={() => setActiveTab(tab.key)}
                      style={{ cursor: "pointer" }}
                    >
                      <span className="inner">{tab.label}</span>
                    </li>
                  ))}
                </ul>
                <div className="widget-content-tab">
                  {TABS.map((tab) => (
                    <div
                      key={tab.key}
                      className={`widget-content-inner${activeTab === tab.key ? " active" : ""}`}
                    >
                      {tab.content}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="flat-spacing-6 pt_0 pb_0">
        <div className="container">
          <div className="row">
            <div className="col-lg-4">
              <div className="collection-item-v4 style-2 hover-img">
                <div className="collection-inner">
                  <a
                    href="/shop?product_type=Romper"
                    className="collection-image img-style radius-0 o-hidden"
                  >
                    <img
                      className=" lazyloaded"
                      data-src="https://huehoppers.s3.ap-south-1.amazonaws.com/website-images/product-details/section1.png"
                      src="https://huehoppers.s3.ap-south-1.amazonaws.com/website-images/product-details/section1.png"
                      alt="collection-img"
                      style={{
                        height: "60vh",
                        width: "100%",
                        border: "2px solid white",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.3)",
                        zIndex: 0,
                        border: "2px solid white",
                      }}
                    />
                  </a>
                  <div
                    className="collection-content wow fadeInUp"
                    data-wow-delay="0s"
                    style={{
                      visibility: "visible",
                      animationDelay: "0s",
                      animationName: "fadeInUp",
                    }}
                  >
                    <h5 className="heading  fs-52 text-white">Fabric</h5>
                    <p
                      className="fade-item fade-item-2 text_white"
                      dangerouslySetInnerHTML={{
                        __html:
                          "Pure Cotton. Breathable. Soft enough for the skin you'd do anything to protect.",
                      }}
                      style={{ display: "block" }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="collection-item-v4 style-2 hover-img">
                <div className="collection-inner">
                  <a
                    href="/shop?product_type=TShirt"
                    className="collection-image img-style radius-0 o-hidden"
                  >
                    <img
                      className=" lazyloaded"
                      data-src="https://huehoppers.s3.ap-south-1.amazonaws.com/website-images/product-details/section2.png"
                      src="https://huehoppers.s3.ap-south-1.amazonaws.com/website-images/product-details/section2.png"
                      alt="collection-img"
                      style={{
                        height: "60vh",
                        width: "100%",
                        border: "2px solid white",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.3)",
                        zIndex: 0,
                        border: "2px solid white",
                      }}
                    />
                  </a>
                  <div
                    className="collection-content wow fadeInUp"
                    data-wow-delay="0s"
                    style={{
                      visibility: "visible",
                      animationDelay: "0s",
                      animationName: "fadeInUp",
                    }}
                  >
                    <h5 className="heading  fs-52 text-white">The Design</h5>
                    <p
                      className="fade-item fade-item-2 text_white"
                      dangerouslySetInnerHTML={{
                        __html:
                          "Illustrated With Intention. Watercolour art. Not a cartoon. Not a sticker. A feeling.",
                      }}
                      style={{ display: "block" }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="collection-item-v4 style-2 hover-img">
                <div className="collection-inner">
                  <a
                    href="/shop?product_type=TShirt"
                    className="collection-image img-style radius-0 o-hidden"
                  >
                    <img
                      className=" lazyloaded"
                      data-src="https://huehoppers.s3.ap-south-1.amazonaws.com/website-images/product-details/section3.png"
                      src="https://huehoppers.s3.ap-south-1.amazonaws.com/website-images/product-details/section3.png"
                      alt="collection-img"
                      style={{
                        height: "60vh",
                        width: "100%",
                        border: "2px solid white",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.3)",
                        zIndex: 0,
                        border: "2px solid white",
                      }}
                    />
                  </a>
                  <div
                    className="collection-content wow fadeInUp"
                    data-wow-delay="0s"
                    style={{
                      visibility: "visible",
                      animationDelay: "0s",
                      animationName: "fadeInUp",
                    }}
                  >
                    <h5 className="heading  fs-52 text-white">The Details</h5>
                    <p
                      className="fade-item fade-item-2 text_white"
                      dangerouslySetInnerHTML={{
                        __html:
                          "Crafted to Last. Colour-fast. Pre-shrunk. Built for a thousand adventures.",
                      }}
                      style={{ display: "block" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="flat-spacing-10">
        <div className="container">
          <div className="tf-grid-layout md-col-2">
            <div className="tf-ourstore-img">
              <img
                className=" ls-is-cached lazyloaded"
                data-src="https://huehoppers.s3.ap-south-1.amazonaws.com/website-images/about/banner.png"
                src="https://huehoppers.s3.ap-south-1.amazonaws.com/website-images/about/banner.png"
                alt="our-store"
              />
            </div>
            <div className="d-flex flex-column justify-content-center">
              <h3
                className="mb_24"
                style={{ fontSize: 32, fontWeight: "bold" }}
              >
                Made Right.
              </h3>
              <div className="mb_20">
                <p style={{ fontSize: 18, lineHeight: 1.5 }}>
                  Every character is conceived with care drawn, redrawn, and
                  refined until it speaks the language only a child truly
                  understands.
                  <br />
                  <br /> Every thread is chosen with intention sourced for
                  softness, tested for endurance, selected because your child's
                  skin deserves nothing less.
                  <br />
                  <br /> Every garment is crafted with the quiet, relentless
                  obsession of two people who built this brand with their own
                  hands and their whole hearts and who will never, not once, not
                  for a single stitch settle for ordinary.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── Tabs ── */}

      {!loading && (
        <RelatedProducts
          collectionSlugs={collections?.map((c) => c.slug)}
          currentProductId={product.id}
          productType={product.product_type}
        />
      )}
      <br />
      <br />
      <br />
      <RecentlyViewed />
      <SizeChartModal
        gender={selectedGender || "male"}
        productType={product?.product_type || "TShirt"}
        sizeChartOpen={sizeChartOpen}
        setSizeChartOpen={setSizeChartOpen}
      />
    </>
  );
};

export default ProductDetail;
